import React, {
    useContext, useEffect, useState, useCallback, useMemo
} from 'react';
import _ from 'lodash';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { WizardPage, wizardProps, WizardPageTemplate } from 'gw-portals-wizard-react';
import {
    ViewModelServiceContext,
    withViewModelService,
    ViewModelForm
} from 'gw-portals-viewmodel-react';
import { TranslatorContext } from '@jutro/locale';
import { useValidation } from 'gw-portals-validation-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { ModalNextProvider } from '@jutro/components';
import Claim from '../../models/Claim';
import Address from '../../models/Address';
import metadata from './ContactDetailsPage.metadata.json5';
import styles from './ContactDetailsPage.module.scss';
import messages from '../../FNOL.messages';

function FNOLContactDetailsPage(props) {
    const translator = useContext(TranslatorContext);
    const { authHeader } = useAuthentication();
    const viewModelService = useContext(ViewModelServiceContext);
    const { AddressLookupService } = useDependencies('AddressLookupService');
    const { wizardData: claimVM, updateWizardData } = props;
    const [availableContacts, setAvailableContacts] = useState([]);
    const [showLookupResult_Ext, updateShowLookupResult_Ext] = useState(false);
    const [showPostCodeLoader, updateshowPostCodeLoader] = useState(false);
    const [lookupResult_Ext, updatelookupResult_Ext] = useState(null);
    const newContact = {
        placeholder: true
    };
    const {
        onValidate,
        isComponentValid,
        initialValidation,
        registerComponentValidation
    } = useValidation('FNOLContactDetailsPage');

    const getAvailableContacts = useCallback(
        (newClaimVM) => {
            const contactsData = newClaimVM.value
                .availableContacts();
                //.filter((contact) => (contact.subtype === 'Person'));
            //contactsData.push(newContact);
            const dropDownContacts = contactsData.map((contact) => {
                const val = contact.placeholder
                    ? translator(messages.otherPerson)
                    : contact.getDisplayName(translator(messages.newContact));
                return {
                    code: val,
                    name: val
                };
            });
            return dropDownContacts;
        },
        [newContact, translator]
    );

    const initializeVM = useCallback(() => {
        const submittingClaimVM = viewModelService.create(
            new Claim(claimVM.value),
            'cc',
            'edgev1141.capabilities.claim.fnol.dto.FnolDTO',
            {
                SubmittingClaim: true
            }
        );
        const newClaimVM = viewModelService.clone(submittingClaimVM);
        updateWizardData(newClaimVM);
        return newClaimVM;
    }, [claimVM, updateWizardData, viewModelService]);

    const isContactNumberCheck = useCallback(() => {
        if (claimVM.mainContact.aspects.valid && claimVM.mainContact.aspects.subtreeValid) {
            return true;
        }
        return false;
    }, [claimVM]);

    useEffect(
        () => {
            const newClaimVM = initializeVM();
            if (_.isEmpty(newClaimVM.value.mainContact)) {
                newClaimVM.value.mainContact = newClaimVM.value.createContact();
                newClaimVM.value.mainContact.primaryPhoneType = 'work';
                newClaimVM.value.mainContact.displayName = translator(messages.newContact);
                newClaimVM.value.mainContact.primaryAddress = new Address();
            }
            const contactsData = newClaimVM.value.availableContacts();
            const placeholders = _.filter(contactsData, (contact) => {
                return contact.placeholder;
            });
            if (placeholders.length === 0) {
                contactsData.push(newContact);
            }
            const getContactList = getAvailableContacts(newClaimVM);
            setAvailableContacts(getContactList);
        },
        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        registerComponentValidation(isContactNumberCheck);
    }, [isContactNumberCheck, registerComponentValidation]);

    const handleContactChange = useCallback(
        (value) => {
            if (value === 'Other Person') {
                claimVM.mainContact.value = claimVM.value.createContact();
                claimVM.mainContact.primaryPhoneType.value = 'work';
                claimVM.mainContact.displayName.value = translator(messages.newContact);
                claimVM.mainContact.primaryAddress.value = new Address();
                const getContactList = getAvailableContacts(claimVM);
                setAvailableContacts(getContactList);
            }
            if (value !== 'Other Person') {
                const selectedMainContact = _.filter(claimVM.value.contacts, (contact) => {
                    if (!contact.placeholder) {
                        const contactName = contact.displayName
                            ? contact.displayName
                            : contact.getDisplayName();
                        return contactName === value;
                    }
                    return false;
                });
                selectedMainContact[0].primaryAddress = !_.isEmpty(
                    selectedMainContact[0].primaryAddress
                )
                    ? selectedMainContact[0].primaryAddress
                    : new Address();
                selectedMainContact[0].displayName = !_.isEmpty(selectedMainContact[0].displayName)
                    ? selectedMainContact[0].displayName
                    : selectedMainContact[0].getDisplayName();
                selectedMainContact[0].primaryPhoneType = !_.isEmpty(selectedMainContact[0].primaryPhoneType)
                    ? selectedMainContact[0].primaryPhoneType
                    : 'work';
                _.set(claimVM, 'mainContact.value', selectedMainContact[0]);
                updateWizardData(claimVM);
            }
        },
        [claimVM, getAvailableContacts, translator, updateWizardData]
    );

    const handleNameChange = useCallback(
        (value, path) => {
            _.set(claimVM, path, value);
            const selectedContactArray = _.filter(claimVM.contacts.value, (contact) => {
                if ('tempID' in contact) {
                    if (contact.tempID === claimVM.mainContact.tempID.value) {
                        return contact;
                    }
                }
                return null;
            });
            const newDisplayName = selectedContactArray[0].getDisplayName(
                translator(messages.newContact)
            );
            _.set(claimVM, 'mainContact.displayName.value', newDisplayName);
            updateWizardData(claimVM);
            const addNew = getAvailableContacts(claimVM);
            setAvailableContacts(addNew);
        },
        [claimVM, getAvailableContacts, translator, updateWizardData]
    );

    const getAddressLineFields = useCallback(() => {
        if (claimVM.value.mainContact && claimVM.value.mainContact.primaryAddress) {
            return (
                <div>
                    <p>{claimVM.value.mainContact.primaryAddress.addressLine1}</p>
                    <p>{claimVM.value.mainContact.primaryAddress.addressLine2}</p>
                    <p>{claimVM.value.mainContact.primaryAddress.addressLine3}</p>
                </div>
            );
        }
        return '';
    }, [claimVM]);

    const prefixTypeList = useMemo(() => {
        const contactdto = viewModelService.create(
            {},
            'cc',
            'edgev1141.capabilities.claim.contact.dto.ContactDTO'
        );
        const filteredList = contactdto.prefix.aspects.availableValues.map((nameprefix) => {
            return {
                code: nameprefix.code,
                name: translator({
                    id: nameprefix.name,
                    defaultMessage: nameprefix.name
                })
            };
        });
        return filteredList;
    }, [translator, viewModelService]);

    const getMultipleLookupResult = useCallback(() => {
        const locationCodes = lookupResult_Ext && lookupResult_Ext.resultSummaries;
        if (locationCodes) {
            return locationCodes.map((locationCode) => {
                return {
                    code: locationCode.addressSummary,
                    name: locationCode.addressSummary
                };
            });
        }
        return [];
    }, [lookupResult_Ext]);

    const isPostCodeValid = () => {
        const newAddress = _.get(claimVM.value, 'mainContact.primaryAddress');
        if (newAddress && newAddress.postalCode) {
            // eslint-disable-next-line max-len
            const postCodeRegex = /^(([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) [0-9][A-Za-z]{2}))$/;
            return !postCodeRegex.test(newAddress.postalCode);
        }
        return true;
    };

    const isUnitedKingdomChosen = () => {
        const newAddress = _.get(claimVM.value, 'mainContact.primaryAddress');
        if (newAddress && newAddress.country) {
            return newAddress.country === 'GB';
        }
        return true;
    };

    const lookupClick = async () => {
        const newAddress = new Address();
        newAddress.postalCode = _.get(claimVM.value, 'mainContact.primaryAddress.postalCode');
        await AddressLookupService.addressLookup(newAddress, authHeader)
            .then((lookupResult) => {
                // TODO Handle for multiple addresses
                /* if (!lookupResult.fetchRequired) {
                    _.set(claimVM.value, 'mainContact.primaryAddress.address', lookupResult.address);
                } */
                if (lookupResult.fetchRequired) {
                    updatelookupResult_Ext(lookupResult);
                    updateShowLookupResult_Ext(true);
                } else {
                    updateShowLookupResult_Ext(false);
                    _.set(claimVM.value, 'mainContact.primaryAddress', lookupResult.address);
                }
                updateWizardData(claimVM);
            }).catch(() => {
                ModalNextProvider.showAlert({
                    title: messages.postCodeLookupErrorTitle,
                    message: translator(messages.postCodeLookupErrorMessage),
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: messages.postCodeLookupErrorConfirmMessage
                }).catch(_.noop);
            });
    };

    const handlePostCodeAddressDropDownValueChange = useCallback(async (addressIndex) => {
        updateshowPostCodeLoader(true);
        const currentLocation = _.get(claimVM, 'mainContact.primaryAddress');
        const updatedLocations = lookupResult_Ext.resultSummaries.find((location) => {
            return location.addressSummary === addressIndex;
        });
        const newAddress = new Address();
        newAddress.postalCode = currentLocation.postalCode.value;
        if (updatedLocations) {
            await AddressLookupService.getAddressFromLookup(updatedLocations, newAddress, authHeader)
                .then((lookupResult) => {
                    _.set(claimVM.value, 'mainContact.primaryAddress', lookupResult);
                    updateWizardData(claimVM);
                    updateshowPostCodeLoader(false);
                    // updateShowLookupResult_Ext(false);
                }).catch(() => {
                    updateshowPostCodeLoader(false);
                    ModalNextProvider.showAlert({
                        title: messages.postCodeLookupErrorTitle,
                        message: translator(messages.postCodeLookupErrorMessage),
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: messages.postCodeLookupErrorConfirmMessage
                    }).catch(_.noop);
                });
        } else {
            updateshowPostCodeLoader(false);
            _.set(claimVM.value, 'mainContact.primaryAddress', newAddress);
        }
    },
    [claimVM, authHeader, updateWizardData, AddressLookupService, translator, lookupResult_Ext]);

    const filteredPreferredPhoneTypes = () => {
        let PREFERRED_CODES = [];
        if (!(_.get(claimVM, 'mainContact.policyRole.value.code') === 'insured')) {
            PREFERRED_CODES = ['work', 'email1', 'mobile'];
        } else {
            PREFERRED_CODES = ['work', 'email1'];
        }
        const contactDto = viewModelService.create(
            {},
            'cc',
            'edgev1141.capabilities.claim.contact.dto.ContactDTO'
        );
        const filteredList = contactDto.primaryPhoneType.aspects.availableValues
            .filter((typeCode) => {
                return PREFERRED_CODES.includes(typeCode.code);
            })
            .map((typeCode) => {
                return {
                    code: typeCode.code,
                    name: translator({
                        id: typeCode.name,
                        defaultMessage: typeCode.name
                    }).replace(/\s/g, '').replace(/1/g, '')
                };
            });
        return filteredList;
    };

    const overrides = {
        '@field': {
            showOptional: false
        },
        namePrefixField: {
            visible: !(_.get(claimVM, 'mainContact.policyRole.value.code') === 'insured'),
            availableValues: prefixTypeList
        },
        emailField: {
            required: _.get(claimVM, 'mainContact.primaryPhoneType') === 'email1'
        },
        mobileNumberField: {
            visible: !(_.get(claimVM, 'mainContact.policyRole.value.code') === 'insured'),
            required: _.get(claimVM, 'mainContact.primaryPhoneType') === 'mobile'
        },
        primaryPhoneField: {
            availableValues : filteredPreferredPhoneTypes(),
            defaultValue: 'work'
        },
        fnolContactField: {
            //defaultValue: defaultAvailableContact,
            availableValues: availableContacts,
            onValueChange: handleContactChange,
            showOptional: false
        },
        postCodeLookupButton: {
            visible: (_.get(claimVM, 'mainContact.policyRole.value.code') !== 'insured') && isUnitedKingdomChosen(),
            disabled: isPostCodeValid()
        },
        firstNameField: {
            visible: !(_.get(claimVM, 'mainContact.policyRole.value.code') === 'insured'),
            onValueChange: handleNameChange
        },
        lastNameField: {
            visible: !(_.get(claimVM, 'mainContact.policyRole.value.code') === 'insured'),
            onValueChange: handleNameChange
        },
        addressField: {
            visible: _.get(claimVM, 'mainContact.policyRole.value.code') === 'insured',
            value: getAddressLineFields()
        },
        PostcodeInlineLoader: {
            loading: showPostCodeLoader,
            loadingMessage: 'Fetching address...',
            visible: showPostCodeLoader
        },
        LossLocationOnPostcode: {
            label: 'Addresses for ' + _.get(claimVM, 'mainContact.primaryAddress.postalCode.value'),
            visible: showLookupResult_Ext && !isPostCodeValid(),
            availableValues: getMultipleLookupResult(),
            onValueChange: (addressIndex) => {
                handlePostCodeAddressDropDownValueChange(addressIndex);
            }
        },
        addressLine1Field: {
            visible: !(_.get(claimVM, 'mainContact.policyRole.value.code') === 'insured')
        },
        addressLine2Field: {
            visible: !(_.get(claimVM, 'mainContact.policyRole.value.code') === 'insured')
        },
        addressLine3Field: {
            visible: !(_.get(claimVM, 'mainContact.policyRole.value.code') === 'insured')
        },
        newContactCityField: {
            readOnly: _.get(claimVM, 'mainContact.policyRole.value.code') === 'insured'
        },
        newContactZipCodeField: {
            visible: isUnitedKingdomChosen(),
            readOnly: _.get(claimVM, 'mainContact.policyRole.value.code') === 'insured'
        },
        newContactStateField: {
            readOnly: _.get(claimVM, 'mainContact.policyRole.value.code') === 'insured'
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            lookupClick: lookupClick

        }
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, claimVM, id, path, overrides);
        },
        [claimVM, overrides]
    );

    if (_.isEmpty(claimVM.value.mainContact)) {
        return null;
    }

    return (
        <WizardPage
            disableNext={!isComponentValid}
            nextButtonID={translator(messages.fnolContactPageNextButton)}
            skipWhen={initialValidation}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                resolveValue={readValue}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
            />
        </WizardPage>
    );
}

FNOLContactDetailsPage.propTypes = wizardProps;
export default withViewModelService(FNOLContactDetailsPage);
